import type { SiteChatLang } from './types';
import { MEDIA_UI, type SiteChatMediaUi } from './uiMedia';

/**
 * Widget chrome the owner does not edit: labels, file errors, aria names. Bundled per language, like the
 * default phrases in `phrases.ts`; these are not admin localization keys — the widget runs on a stranger's
 * site and never talks to the localization service.
 */
type SiteChatBaseUi = {
    policy: string;
    support: string;
    online: string;
    offline: string;
    today: string;
    yesterday: string;
    earlier: string;
    fileTooLarge: string;
    fileFailed: string;
    fileType: string;
    sendFailed: string;
    /** Under a message waiting for the required lead form. */
    heldNote: string;
    skipForm: string;
    /** Titles of the marks under the visitor's messages. */
    delivered: string;
    readMark: string;
    rateLimited: string;
    open: string;
    close: string;
    back: string;
    attach: string;
    send: string;
    removeFile: string;
    hintSample: string;
    sampleQuestion: string;
    sampleAnswer: string;
    sampleLink: string;
    sampleReply: string;
};

export type SiteChatUi = SiteChatBaseUi & SiteChatMediaUi;

const UI: Record<SiteChatLang, SiteChatBaseUi> = {
    en: {
        policy: 'Privacy policy',
        support: 'Support',
        online: 'Online',
        offline: 'Offline',
        today: 'Today',
        yesterday: 'Yesterday',
        earlier: 'Show earlier messages',
        fileTooLarge: 'The file is larger than {size} MB',
        fileFailed: 'The file was not sent',
        fileType: 'This type of file cannot be sent',
        sendFailed: 'Not sent. Tap to try again',
        heldNote: 'Will be sent once you introduce yourself',
        delivered: 'Delivered',
        readMark: 'Read',
        skipForm: 'Skip',
        rateLimited: 'Too many messages. Wait a minute',
        open: 'Open chat',
        close: 'Close',
        back: 'Back',
        attach: 'Attach a file',
        send: 'Send',
        removeFile: 'Remove file',
        hintSample: 'Need help choosing a course?',
        sampleQuestion: 'Hello! Can I pay for the course in instalments?',
        sampleAnswer: 'Good afternoon! Yes, in six monthly payments with no extra charge.',
        sampleLink: 'The terms are here:',
        sampleReply: 'Thanks, I will take a look',
    },
    ru: {
        policy: 'Политика конфиденциальности',
        support: 'Поддержка',
        online: 'В сети',
        offline: 'Не в сети',
        today: 'Сегодня',
        yesterday: 'Вчера',
        earlier: 'Показать ранние сообщения',
        fileTooLarge: 'Файл больше {size} МБ',
        fileFailed: 'Файл не отправился',
        fileType: 'Такой файл отправить нельзя',
        sendFailed: 'Не отправлено. Нажмите, чтобы повторить',
        heldNote: 'Отправим, как только вы представитесь',
        delivered: 'Доставлено',
        readMark: 'Прочитано',
        skipForm: 'Пропустить',
        rateLimited: 'Слишком много сообщений. Подождите минуту',
        open: 'Открыть чат',
        close: 'Закрыть',
        back: 'Назад',
        attach: 'Прикрепить файл',
        send: 'Отправить',
        removeFile: 'Убрать файл',
        hintSample: 'Нужна помощь с выбором курса?',
        sampleQuestion: 'Здравствуйте! Подскажите, есть ли рассрочка на курс?',
        sampleAnswer: 'Добрый день! Да, рассрочка на 6 месяцев без переплаты.',
        sampleLink: 'Условия здесь:',
        sampleReply: 'Спасибо, сейчас проверю',
    },
    uk: {
        policy: 'Політика конфіденційності',
        support: 'Підтримка',
        online: 'У мережі',
        offline: 'Не в мережі',
        today: 'Сьогодні',
        yesterday: 'Учора',
        earlier: 'Показати попередні повідомлення',
        fileTooLarge: 'Файл більший за {size} МБ',
        fileFailed: 'Файл не надіслано',
        fileType: 'Такий файл надіслати не можна',
        sendFailed: 'Не надіслано. Натисніть, щоб повторити',
        heldNote: 'Надішлемо, щойно ви представитеся',
        delivered: 'Доставлено',
        readMark: 'Прочитано',
        skipForm: 'Пропустити',
        rateLimited: 'Забагато повідомлень. Зачекайте хвилину',
        open: 'Відкрити чат',
        close: 'Закрити',
        back: 'Назад',
        attach: 'Прикріпити файл',
        send: 'Надіслати',
        removeFile: 'Прибрати файл',
        hintSample: 'Потрібна допомога з вибором курсу?',
        sampleQuestion: 'Вітаю! Підкажіть, чи є розстрочка на курс?',
        sampleAnswer: 'Добрий день! Так, розстрочка на 6 місяців без переплати.',
        sampleLink: 'Умови тут:',
        sampleReply: 'Дякую, зараз перегляну',
    },
    es: {
        policy: 'Política de privacidad',
        support: 'Soporte',
        online: 'En línea',
        offline: 'Desconectado',
        today: 'Hoy',
        yesterday: 'Ayer',
        earlier: 'Mostrar mensajes anteriores',
        fileTooLarge: 'El archivo supera los {size} MB',
        fileFailed: 'El archivo no se envió',
        fileType: 'Este tipo de archivo no se puede enviar',
        sendFailed: 'No enviado. Toca para reintentar',
        heldNote: 'Se enviará cuando te presentes',
        delivered: 'Entregado',
        readMark: 'Leído',
        skipForm: 'Omitir',
        rateLimited: 'Demasiados mensajes. Espera un minuto',
        open: 'Abrir chat',
        close: 'Cerrar',
        back: 'Atrás',
        attach: 'Adjuntar archivo',
        send: 'Enviar',
        removeFile: 'Quitar archivo',
        hintSample: '¿Necesitas ayuda para elegir un curso?',
        sampleQuestion: '¡Hola! ¿Se puede pagar el curso a plazos?',
        sampleAnswer: '¡Buenas tardes! Sí, en seis cuotas sin recargo.',
        sampleLink: 'Las condiciones están aquí:',
        sampleReply: 'Gracias, lo miro ahora',
    },
    id: {
        policy: 'Kebijakan privasi',
        support: 'Dukungan',
        online: 'Online',
        offline: 'Offline',
        today: 'Hari ini',
        yesterday: 'Kemarin',
        earlier: 'Tampilkan pesan sebelumnya',
        fileTooLarge: 'Ukuran file lebih dari {size} MB',
        fileFailed: 'File tidak terkirim',
        fileType: 'Jenis file ini tidak bisa dikirim',
        sendFailed: 'Tidak terkirim. Ketuk untuk mencoba lagi',
        heldNote: 'Akan dikirim setelah Anda memperkenalkan diri',
        delivered: 'Terkirim',
        readMark: 'Dibaca',
        skipForm: 'Lewati',
        rateLimited: 'Terlalu banyak pesan. Tunggu sebentar',
        open: 'Buka obrolan',
        close: 'Tutup',
        back: 'Kembali',
        attach: 'Lampirkan file',
        send: 'Kirim',
        removeFile: 'Hapus file',
        hintSample: 'Butuh bantuan memilih kursus?',
        sampleQuestion: 'Halo! Apakah kursus bisa dicicil?',
        sampleAnswer: 'Selamat siang! Bisa, enam kali cicilan tanpa biaya tambahan.',
        sampleLink: 'Syaratnya di sini:',
        sampleReply: 'Terima kasih, saya cek dulu',
    },
    lt: {
        policy: 'Privatumo politika',
        support: 'Pagalba',
        online: 'Prisijungę',
        offline: 'Neprisijungę',
        today: 'Šiandien',
        yesterday: 'Vakar',
        earlier: 'Rodyti ankstesnes žinutes',
        fileTooLarge: 'Failas didesnis nei {size} MB',
        fileFailed: 'Failas neišsiųstas',
        fileType: 'Tokio failo išsiųsti negalima',
        sendFailed: 'Neišsiųsta. Spustelėkite, kad bandytumėte dar kartą',
        heldNote: 'Išsiųsime, kai tik prisistatysite',
        delivered: 'Pristatyta',
        readMark: 'Perskaityta',
        skipForm: 'Praleisti',
        rateLimited: 'Per daug žinučių. Palaukite minutę',
        open: 'Atidaryti pokalbį',
        close: 'Uždaryti',
        back: 'Atgal',
        attach: 'Pridėti failą',
        send: 'Siųsti',
        removeFile: 'Pašalinti failą',
        hintSample: 'Reikia pagalbos renkantis kursą?',
        sampleQuestion: 'Sveiki! Ar kursą galima mokėti dalimis?',
        sampleAnswer: 'Laba diena! Taip, šešiomis dalimis be permokos.',
        sampleLink: 'Sąlygos čia:',
        sampleReply: 'Ačiū, tuoj pažiūrėsiu',
    },
    pt: {
        policy: 'Política de privacidade',
        support: 'Suporte',
        online: 'Online',
        offline: 'Offline',
        today: 'Hoje',
        yesterday: 'Ontem',
        earlier: 'Mostrar mensagens anteriores',
        fileTooLarge: 'O ficheiro tem mais de {size} MB',
        fileFailed: 'O ficheiro não foi enviado',
        fileType: 'Este tipo de ficheiro não pode ser enviado',
        sendFailed: 'Não enviado. Toque para tentar de novo',
        heldNote: 'Será enviada assim que você se apresentar',
        delivered: 'Entregue',
        readMark: 'Lida',
        skipForm: 'Pular',
        rateLimited: 'Demasiadas mensagens. Aguarde um minuto',
        open: 'Abrir conversa',
        close: 'Fechar',
        back: 'Voltar',
        attach: 'Anexar ficheiro',
        send: 'Enviar',
        removeFile: 'Remover ficheiro',
        hintSample: 'Precisa de ajuda para escolher um curso?',
        sampleQuestion: 'Olá! É possível pagar o curso em prestações?',
        sampleAnswer: 'Boa tarde! Sim, em seis prestações sem acréscimo.',
        sampleLink: 'As condições estão aqui:',
        sampleReply: 'Obrigado, vou ver já',
    },
    zhcn: {
        policy: '隐私政策',
        support: '客服',
        online: '在线',
        offline: '离线',
        today: '今天',
        yesterday: '昨天',
        earlier: '显示更早的消息',
        fileTooLarge: '文件超过 {size} MB',
        fileFailed: '文件未发送',
        fileType: '无法发送此类文件',
        sendFailed: '未发送。点击重试',
        heldNote: '您介绍自己后即发送',
        delivered: '已送达',
        readMark: '已读',
        skipForm: '跳过',
        rateLimited: '消息太多。请稍等一分钟',
        open: '打开聊天',
        close: '关闭',
        back: '返回',
        attach: '添加文件',
        send: '发送',
        removeFile: '移除文件',
        hintSample: '需要帮您选择课程吗？',
        sampleQuestion: '您好！这门课可以分期付款吗？',
        sampleAnswer: '您好！可以，分 6 个月免息分期。',
        sampleLink: '条件请看这里：',
        sampleReply: '谢谢，我马上看看',
    },
    zhtw: {
        policy: '隱私權政策',
        support: '客服',
        online: '線上',
        offline: '離線',
        today: '今天',
        yesterday: '昨天',
        earlier: '顯示更早的訊息',
        fileTooLarge: '檔案超過 {size} MB',
        fileFailed: '檔案未傳送',
        fileType: '無法傳送此類檔案',
        sendFailed: '未傳送。點擊重試',
        heldNote: '您介紹自己後即傳送',
        delivered: '已送達',
        readMark: '已讀',
        skipForm: '略過',
        rateLimited: '訊息太多。請稍候一分鐘',
        open: '開啟聊天',
        close: '關閉',
        back: '返回',
        attach: '附加檔案',
        send: '傳送',
        removeFile: '移除檔案',
        hintSample: '需要協助選擇課程嗎？',
        sampleQuestion: '您好！這門課可以分期付款嗎？',
        sampleAnswer: '您好！可以，分 6 期免息。',
        sampleLink: '條件請看這裡：',
        sampleReply: '謝謝，我馬上看看',
    },
};

export function siteChatUi(lang: SiteChatLang | undefined): SiteChatUi {
    const key = lang && UI[lang] ? lang : 'en';
    return { ...UI[key], ...MEDIA_UI[key] };
}

/** Maps the widget language to a BCP 47 tag for `Intl` date formatting. */
export function intlLocale(lang: SiteChatLang | undefined): string {
    switch (lang) {
        case 'zhcn': return 'zh-CN';
        case 'zhtw': return 'zh-TW';
        case 'pt': return 'pt-PT';
        default: return lang || 'en';
    }
}
